/* eslint-disable sort-keys-fix/sort-keys-fix */
/** What the history texts are built from (see HistoryEvent in api/types.ts). */
type HistoryEventTexts = {
  description: string
  key?: string | null
  licence?: string | null
  linkedTo?: string | null
  photoNumber?: number | null
  type?: string
}

/** English texts. `fr.ts` must have the same keys: TypeScript checks it. */
export const en = {
  language: {
    switchLabel: 'Afficher le site en français',
    switchText: 'FR'
  },
  layout: {
    navLabel: 'Main',
    search: 'Search',
    api: 'API',
    contribute: 'Contribute',
    footerCode: 'code',
    footerUnder: 'under',
    footerPhotos: 'photos under their own open licences',
    allVessels: 'All vessels',
    dataset: 'Download the dataset',
    apiKey: 'API key',
    review: 'Review'
  },
  common: {
    loading: 'Loading…',
    sending: 'Sending…',
    cancel: 'Cancel',
    optional: '(optional)',
    unnamedVessel: 'Unnamed vessel',
    photos: (count: number) => `${count} photo${count === 1 ? '' : 's'}`,
    noPhotoYet: 'No photo yet',
    copied: 'Copied',
    reject: 'Reject'
  },
  errors: {
    /** No translation: the server's message is shown, it is in English and more precise */
    byCode: {} as Partial<Record<string, string>>,
    generic: 'Something went wrong. Please try again.'
  },
  home: {
    pageTitle: 'Open Vessel Photos — free photos of any vessel, by CFR or MMSI',
    searchPageTitle: (query: string) => `Search: ${query}`,
    title: 'The open library of vessel photos',
    lead: 'Search by name, CFR or MMSI. Every photo is openly licensed and free to reuse, with its credit.',
    searchFormLabel: 'Search vessels',
    searchLabel: 'Search by name, CFR or MMSI',
    searchPlaceholder: 'Name, CFR (FRA000123456) or MMSI (227000101)',
    searchPlaceholderShort: 'Name, CFR or MMSI',
    searchButton: 'Search',
    try: 'Try',
    resultsLabel: 'Results',
    resultsFor: (query: string) => `Results for “${query}”`,
    recentlyAdded: 'Recently added',
    allVessels: 'All vessels',
    searchUnavailable: 'The search is unavailable right now. Please try again later.',
    noMatch: (query: string) => `No vessel matches “${query}”.`,
    noPhotoOfKey: (key: string) => `No photo of ${key} yet.`,
    contributeFirst: 'Send the first one.',
    searchByKey: 'Search by CFR or MMSI to be sure, or',
    contributeAPhoto: 'contribute a photo',
    noPhotoAtAll: 'No photo yet.',
    aboutTitle: 'About',
    aboutText:
      'Open Vessel Photos collects photos of fishing boats and other vessels, each under an open licence, so that anyone can put a face on a name or a registration number. Some come from Wikimedia Commons and Flickr, others were sent by the people who took them.',
    contributeText: 'If you took a photo of a vessel yourself, you can',
    contributeLink: 'add it here',
    contributeEnd: '. It goes online once a volunteer has checked it.',
    apiText:
      "Each photo has a fixed address built from its vessel's CFR or MMSI, so it can be shown on another site or in an app, with no account or key:",
    apiMore: 'Credits, licences and search are covered in the',
    apiLink: 'API documentation',
    apiEnd: '.',
    datasetText: 'The whole dataset, with every credit and licence, can also be',
    datasetLink: 'downloaded as JSON',
    datasetEnd: '.'
  },
  register: {
    title: 'EU fleet register',
    type: 'Type',
    built: 'Built',
    length: 'Length',
    engine: 'Main engine',
    tonnage: 'Tonnage',
    mainGear: 'Main gear',
    otherGears: 'Other gears',
    callSign: 'Call sign',
    port: 'Port of registration',
    /** French vessels are registered in a quartier maritime, the letters of their marking (LR: La Rochelle) */
    frenchPort: 'Port of registration',
    meters: (value: string) => `${value} m`,
    power: (kilowatts: string, horsepower: string) => `${kilowatts} kW (${horsepower} hp)`,
    /** 1 kW = 1.341 mechanical horsepower */
    horsepowerPerKilowatt: 1.341,
    grossTonnage: (value: string) => `${value} GT`,
    yearAndAge: (year: number, age: number) => `${year} (${age} years ago)`,
    source: (date: string | null) =>
      date ? `Source: EU fleet register, last imported on ${date}.` : 'Source: EU fleet register, as last imported.',
    wikidataTitle: 'Vessel data',
    imo: 'IMO number',
    wikidataSource: 'Source: Wikidata (CC0)',
    gears: {
      DRB: 'Boat dredges',
      DRH: 'Hand dredges',
      DRM: 'Mechanised dredges',
      FIX: 'Traps',
      FPO: 'Pots',
      FYK: 'Fyke nets',
      GEN: 'Gillnets and entangling nets',
      GN: 'Gillnets',
      GNC: 'Encircling gillnets',
      GND: 'Drift gillnets',
      GNF: 'Fixed gillnets (on stakes)',
      GNS: 'Set gillnets',
      GTN: 'Combined gillnets and trammel nets',
      GTR: 'Trammel nets',
      HMX: 'Harvesting machines',
      LA: 'Lampara nets',
      LHM: 'Mechanised handlines and pole-lines',
      LHP: 'Handlines and pole-lines',
      LL: 'Longlines',
      LLD: 'Drifting longlines',
      LLS: 'Set longlines',
      LN: 'Lift nets',
      LNB: 'Boat-operated lift nets',
      LNS: 'Shore-operated lift nets',
      LTL: 'Trolling lines',
      LVT: 'Vertical lines',
      LX: 'Hooks and lines',
      MDV: 'Diving',
      MHI: 'Hand implements',
      MIS: 'Miscellaneous gear',
      OT: 'Otter trawls',
      OTB: 'Bottom otter trawls',
      OTM: 'Midwater otter trawls',
      OTT: 'Otter twin trawls',
      PS: 'Purse seines',
      PS1: 'One-boat purse seines',
      PT: 'Pair trawls',
      PTB: 'Bottom pair trawls',
      PTM: 'Midwater pair trawls',
      SB: 'Beach seines',
      SDN: 'Danish seines',
      SPR: 'Pair seines',
      SSC: 'Scottish seines',
      SX: 'Seine nets',
      TB: 'Bottom trawls',
      TBB: 'Beam trawls',
      TBS: 'Bottom shrimp trawls',
      TM: 'Midwater trawls',
      TMS: 'Midwater shrimp trawls',
      TX: 'Other trawls'
    } as Record<string, string>,
    vesselTypes: {
      DO: 'Dredger',
      GO: 'Gillnetter',
      LL: 'Longliner',
      LO: 'Liner',
      LP: 'Pole and line vessel',
      MO: 'Multipurpose vessel',
      NO: 'Lift netter',
      SO: 'Seiner',
      TO: 'Trawler',
      TT: 'Stern trawler',
      WO: 'Trap setter'
    } as Record<string, string>
  },
  chart: {
    commons: 'Commons',
    creditsIntro: 'Photos on the chart:',
    creditsBy: 'by',
    creditsOutro: ', cropped, from Wikimedia Commons.'
  },
  vesselCard: {
    photoAlt: (name: string, credit: string) => `${name}, by ${credit}`
  },
  vessel: {
    source: 'Source',
    related: 'Other vessels',
    relatedFromPort: (port: string) => `Other vessels of ${port}`,
    /** e.g. "LOLA (LR 539755) — 9 photos · Gillnetter · La Rochelle", as the server renders it */
    pageTitle: (vessel: string, photoCount: number, details: string[]) =>
      `${vessel} — ${photoCount ? `${photoCount} photo${photoCount === 1 ? '' : 's'}` : 'vessel photos'}` +
      details.map(detail => ` · ${detail}`).join(''),
    notFoundPageTitle: (key: string) => `No vessel with ${key}`,
    /** e.g. "LOLA is a gillnetter of 14.85 m, built in 1981, registered in La Rochelle…", as the server renders it */
    summary: (s: {
      name: string
      vesselType: string | undefined
      length: string | undefined
      yearBuilt: number | null | undefined
      port: string | null
      country: string | undefined
      mainGear: string | undefined
    }) => {
      const type = s.vesselType?.toLowerCase()
      const place = [s.port && `registered in ${s.port}`, s.country && `under the flag of ${s.country}`]
        .filter(Boolean)
        .join(' ')

      return (
        `${s.name} is ${type ? `${/^[aeiou]/.test(type) ? 'an' : 'a'} ${type}` : 'a vessel'}` +
        (s.length ? ` of ${s.length} m` : '') +
        (s.yearBuilt ? `, built in ${s.yearBuilt}` : '') +
        (place ? `, ${place}` : '') +
        '.' +
        (s.mainGear ? ` Main gear: ${s.mainGear.toLowerCase()}.` : '')
      )
    },
    /** e.g. "Also known as Lee A. Tregurtha" */
    otherNames: (names: string) => `Also known as ${names}`,
    notFoundTitle: (key: string) => `No vessel with ${key} yet`,
    unavailable: 'Unavailable',
    notFoundText: 'Nobody has shared a photo of it so far.',
    contributeFirst: 'Send the first photo',
    invalidKey: (keyType: string) => `This doesn't look like a valid ${keyType}.`,
    backToSearch: 'Back to search',
    tryLater: 'Please try again later.',
    breadcrumb: 'Breadcrumb',
    search: 'Search',
    photoAlt: (vessel: string, vesselType: string | undefined, place: string | null, credit: string) =>
      [vessel, vesselType?.toLowerCase(), place && `at ${place}`].filter(Boolean).join(', ') + `, by ${credit}`,
    photoOf: (n: number, count: number) => `Photo ${n} of ${count}`,
    taken: (date: string) => `taken ${date}`,
    noPhotoOf: (name: string) => `No photo of ${name} yet.`,
    addFirstPhoto: 'Add the first photo',
    photosLabel: 'Photos of this vessel',
    showPhoto: (n: number) => `Show photo ${n}`,
    addPhoto: 'Add a photo',
    reportPhoto: (n: number) => `Report photo ${n}`,
    flag: (flag: string) => `Flag · ${flag} (from CFR)`,
    useThisPhoto: 'Use this photo',
    creditLine: 'Credit line — required by the licence',
    creditLineOptional: ' (optional for this licence, but appreciated)',
    imageSize: 'Image size',
    sizeFull: 'Full',
    imageUrl: 'Image URL, either key works',
    copyCreditAndUrl: 'Copy credit + URL',
    json: 'JSON for this vessel',
    history: 'History',
    historyDate: 'Date',
    historyChange: 'Change',
    historyBy: 'By',
    historyEvent: (event: HistoryEventTexts): string => {
      switch (event.type) {
        case 'VESSEL_ADDED':
          return 'Vessel added'
        case 'PHOTO_PUBLISHED':
          return `Photo ${event.photoNumber} published (${event.licence})`
        case 'KEY_LINKED':
          return event.linkedTo ? `${event.key} linked to ${event.linkedTo}` : `${event.key} added`
        default:
          return event.description
      }
    }
  },
  keyLink: {
    thanks: (keyType: string) => `Thanks! The ${keyType} will be added once a reviewer has checked it.`,
    open: (keyType: string) => `Know its ${keyType}? Suggest it`,
    yourName: 'Your name (optional)',
    submit: (keyType: string) => `Suggest this ${keyType}`
  },
  report: {
    reasons: {
      WRONG_VESSEL: 'It is not this vessel',
      COPYRIGHT: 'It is my photo / not openly licensed',
      PRIVACY: 'It shows people or private details',
      OTHER: 'Other'
    },
    thanks: (n: number) => `Thanks, a reviewer will look at photo ${n}.`,
    title: (n: number) => `Report photo ${n}`,
    reason: 'Reason',
    details: 'Details (optional)',
    submit: 'Send report'
  },
  contribute: {
    pageTitle: 'Contribute a vessel photo',
    cfrHint: '3-letter country code + 9 characters, from the EU fleet register.',
    cfrLabel: 'CFR number',
    mmsiHint: '9 digits, the radio identity shown on AIS.',
    mmsiLabel: 'MMSI number',
    licences: {
      'CC-BY-4.0': 'Anyone can reuse it with your credit.',
      'CC-BY-SA-4.0': 'Reuse with credit; changes shared alike.',
      'CC0-1.0': 'Public domain. No credit required.'
    } as Record<string, string>,
    fileTooLarge: 'This file is larger than 15 MB.',
    thanksTitle: 'Thank you!',
    thanksText: (vessel: string) =>
      `Your photo of ${vessel} is in the review queue. It goes live once a volunteer has checked it.`,
    sendAnother: 'Send another photo',
    backTo: (vessel: string) => `Back to ${vessel}`,
    theVessel: 'the vessel',
    title: 'Contribute a photo',
    lead: "Your own picture, an open licence, and the vessel's CFR or MMSI. It goes live once a volunteer has reviewed it.",
    step1: '1 · Which vessel?',
    keysHint: 'Fill in one of them, or both if you know them.',
    keysConflict: 'This CFR and this MMSI are those of two different vessels: check them.',
    matches: 'Matches',
    anUnnamedVessel: 'an unnamed vessel',
    noVesselYet: 'No vessel with this key yet — it will be created when your photo is approved.',
    vesselName: 'Vessel name',
    vesselNamePlaceholder: 'e.g. Ker Avel',
    step2: '2 · The photo',
    yourUpload: 'Your upload',
    choosePhoto: 'Choose a photo',
    fileHint: 'JPEG or PNG · the vessel clearly visible · 15 MB max',
    dateTaken: 'Date taken',
    place: 'Place',
    placePlaceholder: 'e.g. Concarneau harbour',
    step3: '3 · Credit and licence',
    creditAs: 'Credit as',
    creditPlaceholder: 'Your name or handle',
    licence: 'Licence',
    rights: (licence: string) => `I took this photo, or I hold the right to publish it under ${licence}.`,
    email: 'Email',
    emailHint: '(optional, only seen by reviewers if they have a question)',
    submit: 'Send for review',
    noAccount: 'No account needed.',
    nextTitle: 'What happens next',
    nextReceived: 'Received.',
    nextReceivedText: 'Your photo waits in the review queue.',
    nextReviewed: 'Reviewed.',
    nextReviewedText: 'A volunteer checks the vessel matches its key and the licence is clear.',
    nextPublished: 'Published.',
    nextPublishedText: 'Live on the site and the API under both CFR and MMSI.',
    tipsTitle: 'A good vessel photo',
    tips: [
      'The whole hull in frame, side-on if you can',
      'Name or registration readable',
      "No people's faces up close",
      'Only photos you took yourself'
    ],
    exif: 'Location data (EXIF/GPS) is removed from every photo.',
    scripts: 'Prefer scripts?',
    scriptsEnd: 'does the same.'
  },
  apiKey: {
    pageTitle: 'Request an API key',
    title: 'Request an API key',
    lead: 'Reading the API is free and open. An API key is only needed to send data with a script: contribute photos, suggest keys or report photos through POST requests.',
    received: 'Request received.',
    receivedText: (email: string) => `A reviewer will send your API key to ${email} once your request is approved.`,
    name: 'Your name',
    email: 'Email',
    emailHint: 'The key is sent to this address.',
    organisation: 'Organisation or project',
    use: 'What will you use it for?',
    usePlaceholder: 'e.g. Import the photos taken by our harbour team, about 50 a month.',
    terms: 'I will only send photos their authors agreed to publish under an open licence, with their credit.',
    submit: 'Request a key',
    howTitle: 'How to use it',
    howHeader: 'Send the key in the x-api-key header of your POST requests:',
    howSecret:
      'Keep it secret: never put it in a web page or a mobile app. Everything sent is still reviewed before publication. See the',
    apiDocs: 'API docs'
  },
  docs: {
    pageTitle: 'Free vessel photo API, by CFR or MMSI',
    chip: 'API v1 · no key required',
    title: 'The vessel photo API',
    lead: 'CFR and MMSI are both first-class keys: every URL exists under each of them and returns the same vessel and photos. A vessel can have one key or both — a non-EU ship with only an MMSI works just as well.',
    keyCfr: 'Key · CFR (EU fleet register)',
    keyMmsi: 'Key · MMSI (radio identity)',
    endpoints: 'Endpoints',
    endpointDescriptions: [
      'Vessel, both of its keys, its registration and port, its technical data from the EU fleet register (register: year built, length, engine, tonnage, gears as FAO codes, call sign), all its published photos with credit and licence, and related vessels with photos (same port, else same flag).',
      'The main photo as an image, ready for an <img> tag. ?size=320|800|full (default 800).',
      'A specific photo; the same n under either key is the same file. Same ?size parameter.',
      'Search by name, port, registration, or the beginning of a CFR or MMSI. Up to 24 results.',
      'Vessels with the most recently published photos. ?limit=12 (max 48).',
      'All vessels with photos, by name, 100 per page, with the page count and total. Optional filters: flag (ISO alpha-3, from the CFR or else the MMSI), port, vesselType, mainGear.',
      'The values of these filters, with their number of vessels; flags also have their ISO alpha-2 countryCode.',
      'The whole dataset: every vessel with its photo URLs, credits and licences.',
      'Contribute a photo (multipart: file, credit, licence, takenOn?, place?, email?, contributorId?, vesselName?, plus mmsi? under a CFR or cfr? under an MMSI), under whichever key you know. Published after review. Needs an API key.',
      'The photos of a contributorId sent with your key, published or rejected, whose review was not seen yet: id, status (APPROVED|REJECTED), reviewedAt, cfr, mmsi, vesselName, urls, rejectReason. Needs an API key.',
      'Mark the review of a photo as seen by its contributor: it is no longer returned by the request above. Needs an API key.',
      'Suggest the missing key, e.g. {"keyType": "cfr", "key": "FRA000123456"}. Applied after review. Needs an API key.',
      'Report a photo: {"reason": "WRONG_VESSEL|COPYRIGHT|PRIVACY|OTHER", "message": "…"}. Needs an API key.',
      'Request an API key: {"name", "email", "organisation"?, "intendedUse"}. Sent by email once approved.'
    ],
    example: 'Example',
    errorsIntro: 'Errors come back as',
    errorsOutro: 'with a 400, 404 or 409 status.',
    apiKeyTitle: 'API key',
    apiKeyText1: 'Every GET is open, without key. POST requests need an API key in the x-api-key header:',
    apiKeyLink: 'request one',
    apiKeyText2: ', it is free. Without it, they return a 401.',
    apiKeyContributorId:
      'To tell your users’ photos apart, send contributorId: a stable pseudonymous id of the author in your application (1 to 64 letters, digits, - or _), never an email or a name, e.g. a HMAC of your user id with a secret key that you keep. It is only seen by reviewers. A photo taken on duty by a French administration is sent under the Licence Ouverte 2.0:',
    rateLimitsTitle: 'Rate limits',
    rateLimitsText:
      'Up to 600 GET per minute per IP, and 1,000 POST per hour per API key. Every response tells where you stand in X-RateLimit-Limit, X-RateLimit-Remaining and X-RateLimit-Reset (seconds). Above the limit, requests return a 429 with a Retry-After header. Need more? Download the whole dataset from /v1/export.json, or ask for a higher limit.',
    chips: ['CORS enabled', 'Reading needs no API key', 'Always show the credit'],
    licencesTitle: 'Licences',
    licencesText:
      'Every photo is published by its author under one of these licences. Reuse it freely, as long as you show the credit line given in attribution when the licence asks for it.',
    licenceBy: '— reuse with credit.',
    licenceBySa: '— reuse with credit; changes shared under the same licence.',
    licenceCc0: '— public domain, no credit required.',
    licenceEtalab:
      '— the licence of French administrations, for photos taken on duty: reuse citing the source. Compatible with CC BY 4.0.',
    licenceOlder:
      'Photos imported from Wikimedia Commons or Flickr keep their own licence, which can also be an older version of these (CC BY or CC BY-SA 1.0 to 3.0) or the public domain.',
    selfHostTitle: 'Self-host it',
    selfHostText:
      'The service is one container (Spring Boot serving the API and this web app) and one PostgreSQL database, which also stores the images. Set OPENVESSELPHOTOS_DATABASE_URL and OPENVESSELPHOTOS_ADMIN_PASSWORD, and run it.'
  },
  vessels: {
    pageTitle: (page: number, pageCount: number) =>
      `All vessels with photos${pageCount > 1 ? ` — page ${page} of ${pageCount}` : ''}`,
    title: 'All vessels with photos',
    lead: (total: number) => `${total} vessel${total === 1 ? '' : 's'}, by name.`,
    pagination: 'Pages',
    previous: '← Previous',
    next: 'Next →',
    pageOf: (page: number, pageCount: number) => `Page ${page} of ${pageCount}`,
    filteredTitle: 'Vessels with photos',
    filters: 'Filters',
    flag: 'Flag',
    any: 'All',
    clearFilters: 'Clear filters',
    noMatch: 'No vessel matches these filters.',
    unavailable: 'The list of vessels is unavailable right now. Please try again later.'
  },
  notFound: {
    title: 'Page not found',
    text: "This page doesn't exist.",
    link: 'Search vessel photos'
  },
  admin: {
    pageTitle: 'Review',
    queueTitle: 'Review queue',
    statistics: 'Statistics',
    signOut: 'Sign out',
    loginTitle: 'Review',
    loginText: 'Sign in with the admin password to review contributions.',
    password: 'Admin password',
    signIn: 'Sign in',
    queueLabel: 'Queue',
    tabs: { photos: 'Photos', keyLinks: 'Key links', reports: 'Reports', apiKeys: 'API keys' },
    rejectReasons: [
      "Vessel doesn't match the key",
      "Not the contributor's own photo",
      'Poor quality or vessel not visible',
      'Duplicate of an existing photo',
      'Other'
    ],
    reportReasons: {
      COPYRIGHT: 'Copyright / licence',
      OTHER: 'Other',
      PRIVACY: 'People or private details',
      WRONG_VESSEL: 'Wrong vessel'
    } as Record<string, string>,
    requested: (date: string) => `Requested ${date}`,
    organisation: 'Organisation or project',
    approveKey: 'Approve and create the key',
    apiKeyRequest: 'API key request',
    knownKey: 'Known key',
    suggestedKey: 'Suggested key',
    suggestedBy: (date: string, by: string) => `Suggested ${date} by ${by}`,
    anonymous: 'anonymous',
    linkChecks: ['Both keys found in a public register', 'Same vessel name and flag'],
    linkKeys: 'Link the keys',
    addKey: (key: string) => `Add ${key}`,
    newPhoto: 'New photo',
    firstPhoto: 'First photo · creates vessel',
    reported: (date: string, n: number | null, credit: string, licence: string) =>
      `Reported ${date} · photo ${n} by ${credit} (${licence})`,
    reason: 'Reason',
    removePhoto: 'Remove the photo',
    dismissReport: 'Dismiss the report',
    reportSubtitle: (reason: string, n: number | null) => `Report · ${reason} · photo ${n}`,
    nothingToReview: 'Nothing to review here.',
    pendingItems: 'Pending items',
    newVessel: 'New vessel',
    submitted: (date: string, by: string) => `Submitted ${date} by ${by}`,
    sentWithKey: (name: string) => ` · sent with the API key of ${name}`,
    contributor: (id: string) => ` · contributor ${id}`,
    submittedKey: 'Submitted key',
    licence: 'Licence',
    taken: 'Taken',
    size: 'Size',
    vesselName: 'Vessel name',
    photoChecks: [
      'Vessel in the photo matches the key',
      'Contributor plausibly took it themselves',
      'No close-up faces or private details',
      'Not a duplicate'
    ],
    checklist: 'Checklist',
    rejectWhy: 'If rejecting, why?',
    approvePhoto: 'Approve and publish',
    previewAlt: 'Submission preview',
    keyCreatedFor: (name: string) => `API key created for ${name}`,
    sendItNow: "Send it now: it won't be shown again.",
    emailIt: (email: string) => `Email it to ${email}`,
    copyKey: 'Copy the key',
    done: 'Done',
    emailSubject: 'Your Open Vessel Photos API key',
    emailBody: (name: string, key: string, docsUrl: string) =>
      `Hello ${name},\n\nYour API key request was approved. Here is your key:\n\n${key}\n\nSend it in the x-api-key header of your POST requests, and keep it secret. Documentation: ${docsUrl}\n`,
    activeKeys: 'Active API keys',
    noActiveKey: 'No active key.',
    keyColumns: {
      name: 'Name',
      email: 'Email',
      key: 'Key',
      created: 'Created',
      lastUsed: 'Last used',
      actions: 'Actions'
    },
    revoke: 'Revoke',
    importTitle: 'Import vessels',
    importText1: 'Creates vessels without photos, so that their pages exist and can be found. Accepts an export of the',
    importRegister: 'EU fleet register',
    importText2:
      '(CSV, latest state of the vessels of a country: those retired, destroyed or exported are left out), or a CSV with the columns cfr, mmsi, name, registration, port. Existing vessels are never overwritten: only their missing data is filled in. 15 MB max.',
    csvFile: 'CSV file',
    importing: 'Importing…',
    import: 'Import',
    wikidataImportTitle: 'Complete vessels from Wikidata',
    wikidataImportText:
      'Looks for every vessel with photos and an MMSI on Wikidata (CC0), and saves its IMO number, type, year, length and other names (e.g. a former name), shown on its page. Runs in the background, by batches of 200 vessels; vessels Wikidata does not know keep their data. Can be run again at any time to refresh the data.',
    wikidataImportStart: 'Start',
    wikidataImportProgress: (s: { done: number; total: number; found: number; failed: number }) =>
      `${s.done} / ${s.total} vessels: ${s.found} found on Wikidata, ${s.failed} failed.`,
    photoImportTitle: 'Import photos',
    photoImportText:
      'Publishes openly licensed photos without review, e.g. from Wikimedia Commons. CSV columns: mmsi and/or cfr, vessel_name, author, licence, file_page (page of the original photo), image_1600_url and, optionally, date and note (shown with the photo). Images are downloaded from Wikimedia Commons or Flickr one at a time, in the background: this can take a few hours. Photos already imported are skipped, so the same file can be sent again after an interruption.',
    photoImportRunning: 'Import running…',
    photoImportFinished: 'Last import finished.',
    photoImportProgress: (s: {
      done: number
      total: number
      imported: number
      alreadyImported: number
      failed: number
      invalid: number
    }) =>
      `${s.done} / ${s.total} photos: ${s.imported} imported, ${s.alreadyImported} already there, ${s.failed} failed. ${s.invalid} invalid lines skipped.`,
    stopImport: 'Stop',
    photoNotesTitle: 'Photo notes',
    photoNotesText:
      'Sets the notes shown with published photos, e.g. "Photographed under its former name LE CRI DES FLOTS". CSV columns: cfr (or mmsi), n (the photo number) and note. An empty note removes it.',
    setNotes: 'Set the notes',
    photoNotesResult: (r: { read: number; updated: number; unchanged: number; errors: string[] }) =>
      `${r.read} lines read: ${r.updated} notes set, ${r.unchanged} already up to date, ${r.errors.length} errors.`,
    importResult: (r: {
      read: number
      created: number
      updated: number
      unchanged: number
      skipped: number
      outOfFleet: number
    }) =>
      `${r.read} lines read: ${r.created} vessels created, ${r.updated} completed, ${r.unchanged} already up to date, ${r.outOfFleet} no longer in the fleet, ${r.skipped} skipped.`
  },
  stats: {
    pageTitle: 'Statistics',
    title: 'Statistics',
    backToQueue: '← Review queue',
    period: 'Period',
    lastDays: (days: number) => (days === 365 ? 'Last year' : `Last ${days} days`),
    imageViews: 'Photo views',
    imageViewsElsewhere: (count: string, sites: number) => `${count} on ${sites} other site${sites > 1 ? 's' : ''}`,
    apiCalls: 'API calls',
    apiCallsWithKey: (count: string) => `${count} with an API key`,
    activeKeys: 'Active API keys',
    activeKeysHint: 'Used during the period / all active keys',
    photos: 'Published photos',
    vessels: (count: string) => `${count} vessels`,
    imageViewsPerDay: 'Photo views per day',
    apiCallsPerDay: 'API calls per day',
    vesselsAddedPerDay: (total: string) => `Vessels added per day (${total} in all, imports included)`,
    imageSources: 'Sites showing the photos',
    apiSources: 'API calls by site',
    apiKeys: 'API calls by key',
    noKeyCalls: 'No call with an API key yet.',
    noData: 'Nothing counted yet.',
    site: 'Site',
    count: 'Count',
    key: 'Key',
    calls: 'Calls',
    lastUsed: 'Last used',
    website: 'This website',
    direct: 'Apps and scripts (no referrer)',
    other: 'Other sites',
    note: 'Counted without personal data: images by the site that shows them (Referer header), API calls by key. A browser keeps a photo for a day, so it is counted once a day per visitor at most; the last minute is not counted yet.'
  }
}

export type Messages = typeof en
